import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

import { Dialog, Field, List, Button, Alert } from '@ctct/ui-react';

import { getPartnerOwnerSummary } from '../../../api/DistUI';
import MyAccountApiLite from '../../../api/MyAccountApiLite';
import SettingsSvc from '../../../api/SettingsSvc';
import { ACCOUNT_MANAGER, CAMPAIGN_CREATOR, BP_CONSOLE } from '../../../constants/Roles';

const propTypes = {
  userUuid: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  userRole: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

const EditRoleDialog = ({ name, userRole, t, userUuid, onClose, onSuccess }) => {
  const [role, setRole] = useState(userRole);
  const [error, setError] = useState(false);
  const [canInvitePartnerManager, setCanInvitePartnerManager] = useState(false);
  const onSubmit = async () => {
    try {
      await MyAccountApiLite.setRole(userUuid, role);
      onSuccess();
    } catch (e) {
      setError(true);
    }
  };

  const validateCanInvitePartnerManager = async () => {
    if (!(await getPartnerOwnerSummary())) {
      setCanInvitePartnerManager(false);

      return;
    }

    const { enabled: localPartnerUIEnabled = false } = await SettingsSvc.getFeatureSetting(
      'bpconsole',
      'enable_partner_tab_override'
    );

    setCanInvitePartnerManager(localPartnerUIEnabled);
  };

  useEffect(() => {
    validateCanInvitePartnerManager();
  }, []);

  const roleList = useMemo(() => {
    const roles = [
      {
        // ctct-ui-react List.items[].value asks for string
        value: `${ACCOUNT_MANAGER}`,
        heading: t('add_user.account_manager.title'),
        text: t('add_user.account_manager.description'),
      },
      {
        value: `${CAMPAIGN_CREATOR}`,
        heading: t('add_user.campaign_creator.title'),
        text: t('add_user.campaign_creator.description'),
      },
    ];

    if (canInvitePartnerManager) {
      roles.push({
        value: `${BP_CONSOLE}`,
        heading: t('add_user.bp_console.title'),
        text: t('add_user.bp_console.description'),
      });
    }

    return roles;
  }, [canInvitePartnerManager]);

  return (
    <Dialog
      autoFocus={false}
      data-qe-id="edit-role-dialog"
      isOpen
      heading={t('users.edit_role.heading')}
      footerActions={
        <>
          <Button text={t('users.edit_role.cancel')} onClick={onClose} data-qe-id="edit-user-cancel-button" />
          <Button text={t('users.edit_role.ok')} kind="primary" onClick={onSubmit} data-qe-id="edit-user-ok-button" />
        </>
      }
    >
      {error && <Alert message={t('users.edit_role.error')} kind="error" />}
      <p>{t('users.edit_role.text', { name })}</p>
      <Field label={t('users.edit_role.role_label')} required data-qe-id="edit-user-role">
        <List
          outlined
          id="radio-role"
          data-qe-id="roles-list"
          // ctct-ui-react List.value asks for string
          value={`${role}`}
          kind="radio"
          name="role_checkbox"
          onChoose={(_e, data) => setRole(Number(data.item.value))}
          controlLocation="left"
          items={roleList}
        />
      </Field>
    </Dialog>
  );
};

EditRoleDialog.propTypes = propTypes;

export { EditRoleDialog };

export default EditRoleDialog;
